import styles from 'css/src/components/app/doc-to-script/VoiceSelectionModalV2.module.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AIVoiceSpeed from './AIVoiceSpeed';
import type { VoiceSelectionModalPresentationProps } from './types';

import { type AIVoice, Urls } from 'js/src/client';
import useAIVoiceModal from 'js/src/components/app/VoiceSelectionModal/useAIVoiceModal';
import VoiceSelectionModalResults from 'js/src/components/app/VoiceSelectionModal/VoiceSelectionModalResults';
import VoiceSelectionModalSearch from 'js/src/components/app/VoiceSelectionModal/VoiceSelectionModalSearch';
import { PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import { getCanUsePremiumAIVoices } from 'js/src/data/account';
import { ensureAIVoiceLoaded, getAIVoiceById } from 'js/src/data/ai-voices-and-avatars';
import { getAIVoiceModalVoiceById } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-selectors';
import { resetAIVoiceModal } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import { setAIVoices } from 'js/src/data/ai-voices-and-avatars/ai-voices/ai-voices-slices';
import { hideModal, VOICE_SELECTION_MODAL_SOURCE } from 'js/src/data/modals';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

const VoiceSelectionModalPresentationV2 = ({
    isShowing,
    source = null,
    initialSelectedVoiceId = null,
    initialVoiceSpeed = 1,
    onApply,
    onRemoveVoiceover,
    onBack,
}: VoiceSelectionModalPresentationProps) => {
    const dispatch = useAppDispatch();

    const { isLoading } = useAIVoiceModal();

    const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(initialSelectedVoiceId);
    const [selectedSpeed, setSelectedSpeed] = useState<number | null>(initialVoiceSpeed);

    useEffect(() => {
        setSelectedVoiceId(initialSelectedVoiceId);
    }, [initialSelectedVoiceId]);

    useEffect(() => {
        setSelectedSpeed(initialVoiceSpeed);
    }, [initialVoiceSpeed]);

    const canUsePremiumAIVoices = useSelector(getCanUsePremiumAIVoices);
    const selectedVoice = useSelector((state: RootState) => {
        if (!selectedVoiceId) return null;
        return (
            getAIVoiceModalVoiceById(state, selectedVoiceId) ??
            getAIVoiceById(state, selectedVoiceId)
        );
    });

    useEffect(() => {
        if (selectedVoiceId && !selectedVoice) {
            dispatch(ensureAIVoiceLoaded(selectedVoiceId));
        }
    }, [dispatch, selectedVoiceId, selectedVoice]);

    const canApply = useMemo(
        () => !(selectedVoice && selectedVoice.is_paid && !canUsePremiumAIVoices),
        [canUsePremiumAIVoices, selectedVoice],
    );

    const closeModal = useCallback(() => {
        dispatch(hideModal());
        dispatch(resetAIVoiceModal());
    }, [dispatch]);

    const onApplyDefault = useCallback(
        (voice: AIVoice, newVoiceSpeed: number | null) => {
            closeModal();
        },
        [closeModal],
    );

    const handleVoiceSelected = useCallback(
        (voice: AIVoice) => {
            setSelectedVoiceId(voice.slug);
            analytics.track('Voice selected', {
                voice: voice.display_name,
                kind: voice.is_paid ? 'premium' : 'free',
                source: source,
                is_deprecated: voice.is_deprecated,
            });
        },
        [source],
    );

    const handleSpeedChange = useCallback((speed: number) => {
        setSelectedSpeed(speed);
    }, []);

    const handleCloseModal = useCallback(() => {
        closeModal();
    }, [closeModal]);

    const handleBack = useCallback(() => {
        dispatch(resetAIVoiceModal());
        onBack?.();
    }, [dispatch, onBack]);

    const handleRemoveVoiceover = useCallback(() => {
        onRemoveVoiceover?.();
    }, [onRemoveVoiceover]);

    const handleApply = useCallback(() => {
        if (!selectedVoice) {
            throw new Error('No voice selected');
        }

        // always add selected voice to ai voices
        dispatch(setAIVoices([selectedVoice]));

        if (canApply) {
            (onApply ?? onApplyDefault)(selectedVoice, selectedSpeed);
            return;
        }

        // for consistency with upsell modals,
        // similar analytics events that are used in feature-lock-modal
        const context = 'AI_VOICE_MODAL';
        trackUpsellClicked(context, {
            voice: selectedVoice.display_name,
            subCategory: 'AI Voiceover',
            source,
        });

        // open upsell page in new tab
        window.open(Urls.PRICING, '_blank');
    }, [selectedVoice, dispatch, canApply, source, onApply, onApplyDefault, selectedSpeed]);

    const showRemoveVoiceoverButton = !!onRemoveVoiceover;
    const showSpeedSelector =
        source === VOICE_SELECTION_MODAL_SOURCE.TRANSCRIPT_TITLE ||
        source === VOICE_SELECTION_MODAL_SOURCE.ANIMATION_EDITOR;

    return (
        <Modal
            className={styles['voice-selection-modal-v2']}
            title="Select an AI voice"
            size={MODAL_SIZES.LG}
            showing={isShowing}
            onHidden={handleCloseModal}
            onMoveBack={onBack ? handleBack : undefined}
            footer={
                <div className="container-fluid px-l5-24 py-l5-16 m-l5-0">
                    <div className="row gx-2">
                        {showSpeedSelector && (
                            <div className="col-auto">
                                <AIVoiceSpeed
                                    selectedSpeed={selectedSpeed}
                                    onSpeedChange={handleSpeedChange}
                                />
                            </div>
                        )}
                        {showRemoveVoiceoverButton && (
                            <div className="col-auto">
                                <SecondaryButton
                                    text="Remove voiceover"
                                    iconLeft="voice_over_off"
                                    onClick={handleRemoveVoiceover}
                                />
                            </div>
                        )}
                        <div className="col" />
                        <div className="col-auto">
                            <SecondaryButton onClick={handleCloseModal} text="Cancel" />
                        </div>
                        <div className="col-auto">
                            <Tooltip
                                title={
                                    canApply
                                        ? ''
                                        : 'Upgrade to the Basic plan or above to apply this voice'
                                }
                                trigger="hover"
                                placement={Placement.RIGHT}
                            >
                                <PrimaryButton
                                    text={canApply ? 'Apply' : 'Upgrade to apply'}
                                    disabled={!selectedVoice}
                                    onClick={handleApply}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </div>
            }
            showFooterDivider
            noFooterPadding
            loadingContents={isLoading}
            bodyClassName="min-height-0 d-flex flex-column"
            noPadding
            unsetOverflow
        >
            <VoiceSelectionModalSearch
                className="px-l5-24 py-l5-16"
                selectedVoice={selectedVoice}
            />
            <VoiceSelectionModalResults
                className="d-flex flex-column flex-grow-1 min-height-0 px-l5-24 pb-l5-16"
                selectedVoice={selectedVoice}
                onVoiceSelected={handleVoiceSelected}
                canUsePremiumAIVoices={canUsePremiumAIVoices}
                source={source}
            />
        </Modal>
    );
};

export default VoiceSelectionModalPresentationV2;
